/**
 * mediaStorage.js
 *
 * IndexedDB persistence layer for projectMedia assets.
 *
 * Why IndexedDB (not localStorage)?
 *  - localStorage is limited to ~5-10 MB per origin — one 4K reference image
 *    can blow past that.
 *  - IndexedDB can store gigabytes and natively handles File / Blob objects as
 *    binary data, so a 200 MB source video is stored efficiently without any
 *    base64 encoding overhead.
 *
 * Blob-URL strategy
 *  - Blob URLs (blob://…) are generated by the browser and only live for the
 *    current session — they die on page reload.
 *  - We save the raw Blob/File under `asset.blobData`.
 *  - On load we call URL.createObjectURL(blobData) to resurrect a fresh URL.
 *  - Remote URLs (https://…, data:…) are stored as-is.
 */

const DB_NAME    = 'helix_media_v1';
const DB_VERSION = 2;
const STORE      = 'assets';

/** Fields added in v2 for context-filing (characters, objects, projects, tags). */
const CONTEXT_DEFAULTS = {
    linkedObjectIds:    [],
    linkedCharacterIds: [],
    linkedProjectIds:   [],
    tags:               [],
};

let _db = null;

/** Opens (or reuses) the IndexedDB connection. */
const openDB = () =>
    new Promise((resolve, reject) => {
        if (_db) return resolve(_db);

        const req = indexedDB.open(DB_NAME, DB_VERSION);

        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'id' });
            }

            // v1 -> v2: backfill context-filing arrays on every existing record
            if (e.oldVersion < 2) {
                const tx = e.target.transaction;
                const store = tx.objectStore(STORE);
                const cursor = store.openCursor();
                cursor.onsuccess = (ce) => {
                    const c = ce.target.result;
                    if (!c) return;
                    const rec = c.value;
                    let dirty = false;
                    for (const [key, val] of Object.entries(CONTEXT_DEFAULTS)) {
                        if (!Array.isArray(rec[key])) { rec[key] = val; dirty = true; }
                    }
                    if (dirty) c.update(rec);
                    c.continue();
                };
            }
        };

        req.onsuccess = (e) => {
            _db = e.target.result;
            resolve(_db);
        };

        req.onerror = (e) => reject(e.target.error);
    });

/**
 * Persist the current projectMedia array.
 * Items whose url starts with "blob:" must carry a `blobData` field so
 * we can recreate the URL on the next load; otherwise they are skipped.
 */
export const saveMediaAssets = async (assets) => {
    try {
        const db    = await openDB();
        const tx    = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);

        // Wipe then re-write — keeps things simple and avoids stale entries
        store.clear();

        for (const asset of assets) {
            const isBlob   = asset.url?.startsWith('blob:');
            const hasBData = !!asset.blobData;

            if (isBlob && !hasBData) {
                // Blob URL with no stored file data → skip (can't recreate)
                continue;
            }

            // Strip the ephemeral blobUrl from the stored record; we'll
            // regenerate it on load.  Keep everything else intact.
            // Ensure context-filing arrays are always present.
            const toStore = { ...CONTEXT_DEFAULTS, ...asset };
            if (isBlob) {
                toStore.url = null; // will be regenerated
            }
            store.put(toStore);
        }

        await new Promise((res, rej) => {
            tx.oncomplete = res;
            tx.onerror    = () => rej(tx.error);
        });
    } catch (e) {
        console.warn('[mediaStorage] save failed:', e);
    }
};

/**
 * Load persisted assets and recreate blob URLs where needed.
 * Returns an array ready to drop straight into projectMedia state.
 */
export const loadMediaAssets = async () => {
    try {
        const db    = await openDB();
        const tx    = db.transaction(STORE, 'readonly');
        const store = tx.objectStore(STORE);

        const raw = await new Promise((res, rej) => {
            const req    = store.getAll();
            req.onsuccess = () => res(req.result || []);
            req.onerror   = () => rej(req.error);
        });

        return raw.map((asset) => {
            if (!asset.url && asset.blobData) {
                // Recreate a fresh blob URL from the stored File / Blob
                try {
                    return { ...asset, url: URL.createObjectURL(asset.blobData) };
                } catch {
                    return null; // corrupted entry — drop it
                }
            }
            return asset;
        }).filter(Boolean);
    } catch (e) {
        console.warn('[mediaStorage] load failed:', e);
        return [];
    }
};

/**
 * Update a single asset record in IndexedDB by ID.
 * Only touches the fields in `updates`, preserving everything else.
 */
export const updateMediaAssetInDB = async (id, updates) => {
    try {
        const db    = await openDB();
        const tx    = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);

        const existing = await new Promise((res, rej) => {
            const req = store.get(id);
            req.onsuccess = () => res(req.result);
            req.onerror   = () => rej(req.error);
        });
        if (!existing) return;

        store.put({ ...existing, ...updates });
        await new Promise((res, rej) => {
            tx.oncomplete = res;
            tx.onerror    = () => rej(tx.error);
        });
    } catch (e) {
        console.warn('[mediaStorage] updateMediaAssetInDB failed:', e);
    }
};

// ── Timeline clip + storyboard edge persistence (localStorage) ───────────────
// Clips are serializable JSON; blob:// URL fields are stripped before saving
// since those URLs expire on page reload. A _pmId field in the clip is used to
// cross-reference projectMedia and restore the fresh blob URL on the next load.

const CLIP_STATE_KEY = 'helix_clip_state_v1';
const EDGE_STATE_KEY = 'helix_edge_state_v1';

// Fields that may hold ephemeral blob:// URLs
const BLOB_FIELDS = ['media', 'sourceMedia', 'startFrame', 'endFrame', 'endMedia'];

// Fields that hold auto-extracted frame data URLs (~30-50KB each base64).
// These are produced by extractVideoFrames() from clip.media and are
// re-extractable on load, so we strip them before persisting to keep the
// localStorage payload under the ~5MB origin quota. NodeStoryboard's
// resolveStartFrame / resolveEndFrame helpers re-extract on demand when a
// clip has video media but missing frames.
const FRAME_FIELDS = ['startFrame', 'endFrame'];

/**
 * Strip ephemeral / oversized fields from a clip so it fits in localStorage.
 * - blob: URLs in BLOB_FIELDS are nulled (recreated via _pmId on load).
 * - data: URL frames are nulled (re-extracted from clip.media on demand).
 * - generations history is filtered to http(s) entries only.
 * - referenceImages drop blob entries.
 */
export const sanitizeClipForStorage = (clip) => {
    const c = { ...clip };
    for (const f of BLOB_FIELDS) {
        if (typeof c[f] === 'string' && c[f].startsWith('blob:')) c[f] = null;
    }
    for (const f of FRAME_FIELDS) {
        if (typeof c[f] === 'string' && c[f].startsWith('data:')) c[f] = null;
    }
    if (Array.isArray(c.generations)) {
        c.generations = c.generations.filter(g => {
            if (!g || typeof g.url !== 'string') return false;
            return !g.url.startsWith('data:') && !g.url.startsWith('blob:');
        });
    }
    if (Array.isArray(c.referenceImages)) {
        c.referenceImages = c.referenceImages.filter(u => typeof u !== 'string' || !u.startsWith('blob:'));
    }
    return c;
};

/**
 * Persist the current timelineClips and storyboardEdges to localStorage.
 * Blob URL fields are nulled out; _pmId preserves the cross-reference so
 * they can be restored on the next load via restoreClipMedia().
 */
export const saveClipState = (clips, edges) => {
    try {
        const serializable = clips.map(sanitizeClipForStorage);
        localStorage.setItem(CLIP_STATE_KEY, JSON.stringify(serializable));
        localStorage.setItem(EDGE_STATE_KEY, JSON.stringify(edges));
    } catch (e) {
        console.warn('[mediaStorage] saveClipState failed:', e);
    }
};

/**
 * Load persisted clips and edges from localStorage.
 * Returns { clips, edges } — either may be null if nothing was saved.
 */
export const loadClipState = () => {
    try {
        const clips = JSON.parse(localStorage.getItem(CLIP_STATE_KEY));
        const edges = JSON.parse(localStorage.getItem(EDGE_STATE_KEY));
        return {
            clips: Array.isArray(clips) ? clips : null,
            edges: Array.isArray(edges) ? edges : null,
        };
    } catch (e) {
        console.warn('[mediaStorage] loadClipState failed:', e);
        return { clips: null, edges: null };
    }
};

/**
 * After both clips and projectMedia are loaded, restore blob URLs in clips
 * by cross-referencing each clip's _pmId against the restored projectMedia array.
 * Called once during the mount sequence, after IndexedDB assets are loaded.
 */
export const restoreClipMedia = (clips, projectMedia) => {
    if (!projectMedia.length) return clips;
    const pmMap = new Map(projectMedia.map(m => [m.id, m.url]));
    return clips.map(clip => {
        if (!clip._pmId) return clip;
        const freshUrl = pmMap.get(clip._pmId);
        if (!freshUrl) return clip;
        // Restore any null blob fields using the fresh URL from IndexedDB
        return {
            ...clip,
            media:       clip.media       ?? freshUrl,
            sourceMedia: clip.sourceMedia ?? freshUrl,
            startFrame:  clip.startFrame  ?? freshUrl,
        };
    });
};
